import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Award, Save, Eye, Download, RefreshCw, Image, Type, Palette, Layout, Upload, Plus, Trash2, Users, FileSpreadsheet, User, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import axios from 'axios';
import { toast } from 'sonner';
import getImageUrl from '@/utils/imageUrl';

const API = `${API_URL}/api`;

// Template style configurations
const templateStyles = {
  formal: {
    name: 'Formal / Traditional',
    description: 'Classic design with ornate borders and elegant typography',
    preview: 'border-double border-8 border-amber-600'
  },
  modern: {
    name: 'Modern',
    description: 'Clean lines with minimal decoration and contemporary feel',
    preview: 'border-2 border-gray-400'
  },
  elegant: {
    name: 'Elegant',
    description: 'Sophisticated design with subtle gradients and refined elements',
    preview: 'border-4 border-amber-400 shadow-lg'
  },
  minimal: {
    name: 'Minimal',
    description: 'Simple and understated with focus on content',
    preview: 'border border-gray-300'
  }
};

const CertificateDesigner = () => {
  const { user } = useOutletContext();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [nominees, setNominees] = useState([]);
  const [selectedNominees, setSelectedNominees] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState({});
  const [bulkTab, setBulkTab] = useState('event'); // 'event', 'members', 'excel'
  
  const fileInputRefs = useRef({});

  // Certificate design settings
  const [design, setDesign] = useState({
    // Template Style
    template_style: 'formal', // formal, modern, elegant, minimal
    
    // Size & Orientation
    size: 'a4', // a4, letter, custom
    orientation: 'landscape', // landscape, portrait
    custom_width: 297, // mm
    custom_height: 210, // mm
    
    // Background
    background_color: '#FFFEF7',
    background_image_url: '',
    border_style: 'ornate', // none, simple, ornate, double
    border_color: '#D4AF37',
    border_width: 8,
    
    // Logos (multiple)
    logos: [
      { id: 'logo1', url: '', position: 'top-left', size: 80 },
      { id: 'logo2', url: '', position: 'top-right', size: 80 },
    ],
    
    // Header
    organization_name: 'Lions Club International',
    chapter_name: 'Jaipur Hawamahal',
    show_header: true,
    
    // Title
    certificate_title: 'Certificate of Appreciation',
    title_color: '#0F172A',
    title_font_size: 48,
    
    // Body Content
    intro_text: 'This is to certify that',
    recipient_placeholder: '{RECIPIENT_NAME}',
    body_text: 'has been awarded the',
    award_placeholder: '{AWARD_CATEGORY}',
    conclusion_text: 'in recognition of their outstanding contribution and dedication.',
    body_color: '#1E293B',
    body_font_size: 18,
    
    // Recipient Photo
    show_recipient_photo: false,
    recipient_photo_size: 100, // px
    recipient_photo_position: 'top-center', // top-center, left, right
    recipient_photo_shape: 'circle', // circle, square, rounded
    
    // Date
    show_date: true,
    date_format: 'full', // full, short
    date_position: 'bottom-center',
    
    // Signatories (multiple)
    signatories: [
      { id: 'sig1', name: '', designation: 'President', signature_url: '' },
      { id: 'sig2', name: '', designation: 'Secretary', signature_url: '' },
    ],
    show_signature_lines: true,
    
    // Seal
    show_seal: true,
    seal_text: 'LIONS CLUB',
    seal_image_url: '',
    seal_size: 80,
    seal_position: 'bottom-right',
    
    // Decorative
    accent_color: '#D4AF37',
    show_decorative_border: true,
  });

  // Excel data for bulk generation
  const [excelData, setExcelData] = useState([]);
  const excelInputRef = useRef(null);

  useEffect(() => {
    loadEvents();
    loadSavedDesign();
    loadMembers();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      loadNominees();
    }
  }, [selectedEvent]);

  const loadEvents = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0].event_id);
      }
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNominees = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/forms/submissions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const eventNominees = response.data.filter(s => s.event_id === selectedEvent);
      setNominees(eventNominees);
    } catch (error) {
      console.error('Failed to load nominees:', error);
    }
  };

  const loadMembers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/members?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data || []);
    } catch (error) {
      console.error('Failed to load members:', error);
    }
  };

  const loadSavedDesign = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/settings/certificate-design`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setDesign(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      // Design not saved yet
    }
  };

  const handleSaveDesign = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/settings/certificate-design`, design, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Certificate design saved!');
    } catch (error) {
      toast.error('Failed to save design');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (type, id, file) => {
    if (!file) return;
    
    const uploadKey = `${type}_${id}`;
    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    
    try {
      const token = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const url = response.data.url;
      
      if (type === 'logo') {
        setDesign(prev => ({
          ...prev,
          logos: prev.logos.map(l => l.id === id ? { ...l, url } : l)
        }));
      } else if (type === 'signature') {
        setDesign(prev => ({
          ...prev,
          signatories: prev.signatories.map(s => s.id === id ? { ...s, signature_url: url } : s)
        }));
      } else if (type === 'background') {
        setDesign(prev => ({ ...prev, background_image_url: url }));
      } else if (type === 'seal') {
        setDesign(prev => ({ ...prev, seal_image_url: url }));
      }
      
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload image: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Logo management
  const addLogo = () => {
    const newLogo = {
      id: `logo_${Date.now()}`,
      url: '',
      position: 'top-center',
      size: 80
    };
    setDesign(prev => ({ ...prev, logos: [...prev.logos, newLogo] }));
  };

  const removeLogo = (id) => {
    setDesign(prev => ({
      ...prev,
      logos: prev.logos.filter(l => l.id !== id)
    }));
  };

  const updateLogo = (id, field, value) => {
    setDesign(prev => ({
      ...prev,
      logos: prev.logos.map(l => l.id === id ? { ...l, [field]: value } : l)
    }));
  };

  // Signatory management
  const addSignatory = () => {
    const newSig = {
      id: `sig_${Date.now()}`,
      name: '',
      designation: 'Member',
      signature_url: ''
    };
    setDesign(prev => ({ ...prev, signatories: [...prev.signatories, newSig] }));
  };

  const removeSignatory = (id) => {
    setDesign(prev => ({
      ...prev,
      signatories: prev.signatories.filter(s => s.id !== id)
    }));
  };

  const updateSignatory = (id, field, value) => {
    setDesign(prev => ({
      ...prev,
      signatories: prev.signatories.map(s => s.id === id ? { ...s, [field]: value } : s)
    }));
  };

  // Excel upload for bulk generation
  const handleExcelUpload = async (file) => {
    if (!file) return;
    
    try {
      // Parse Excel file (simplified - in production use xlsx library)
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const rows = text.split('\n').map(row => row.split(','));
        const headers = rows[0];
        const data = rows.slice(1).filter(row => row[0]).map(row => {
          const obj = {};
          headers.forEach((h, i) => {
            obj[h.trim().toLowerCase()] = row[i]?.trim() || '';
          });
          return obj;
        });
        setExcelData(data);
        toast.success(`Loaded ${data.length} records from file`);
      };
      reader.readAsText(file);
    } catch (error) {
      toast.error('Failed to parse Excel file');
    }
  };

  const handleSelectAllNominees = () => {
    if (selectedNominees.length === nominees.length) {
      setSelectedNominees([]);
    } else {
      setSelectedNominees(nominees.map(n => n.submission_id));
    }
  };

  const toggleNomineeSelection = (id) => {
    setSelectedNominees(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAllMembers = () => {
    if (selectedMembers.length === members.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members.map(m => m.user_id));
    }
  };

  const toggleMemberSelection = (id) => {
    setSelectedMembers(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Download single certificate PDF
  const handleDownloadSinglePDF = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/certificates/preview`, {
        params: {
          name: design.preview_name || 'Sample Recipient',
          award: design.preview_category || 'Excellence Award'
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${design.preview_name || 'sample'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download certificate');
    }
  };

  // Bulk generate and email certificates
  const handleBulkGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('session_token');
      
      let sourceType = bulkTab;
      let sourceIds = [];
      let customData = [];
      
      if (bulkTab === 'event') {
        sourceType = 'event';
        sourceIds = selectedNominees;
      } else if (bulkTab === 'members') {
        sourceType = 'members';
        sourceIds = selectedMembers;
      } else if (bulkTab === 'excel') {
        sourceType = 'custom';
        customData = excelData;
      }
      
      const response = await axios.post(`${API}/certificates/generate-bulk`, {
        source_type: sourceType,
        source_ids: sourceIds,
        custom_data: customData,
        send_email: true,
        email_subject: 'Your Certificate from Lions Club Jaipur Hawamahal',
        email_body: 'Congratulations! Please find your certificate attached to this email.'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowBulkDialog(false);
    } catch (error) {
      console.error('Bulk generate error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate certificates');
    } finally {
      setGenerating(false);
    }
  };

  // Download all certificates as ZIP
  const handleDownloadAllCertificates = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('session_token');
      
      let sourceType = bulkTab;
      let sourceIds = [];
      let customData = [];
      
      if (bulkTab === 'event') {
        sourceType = 'event';
        sourceIds = selectedNominees;
      } else if (bulkTab === 'members') {
        sourceType = 'members';
        sourceIds = selectedMembers;
      } else if (bulkTab === 'excel') {
        sourceType = 'custom';
        customData = excelData;
      }
      
      const response = await axios.post(`${API}/certificates/download-all`, {
        source_type: sourceType,
        source_ids: sourceIds,
        custom_data: customData,
        send_email: false
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificates_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificates downloaded!');
      setShowBulkDialog(false);
    } catch (error) {
      console.error('Download all error:', error);
      toast.error('Failed to download certificates');
    } finally {
      setGenerating(false);
    }
  };

  const getCertificateDimensions = () => {
    if (design.size === 'custom') {
      return { width: design.custom_width, height: design.custom_height };
    }
    const sizes = {
      a4: { landscape: { width: 297, height: 210 }, portrait: { width: 210, height: 297 } },
      letter: { landscape: { width: 279, height: 216 }, portrait: { width: 216, height: 279 } },
    };
    return sizes[design.size]?.[design.orientation] || sizes.a4.landscape;
  };

  const dimensions = getCertificateDimensions();
  const aspectRatio = dimensions.width / dimensions.height;

  const borderStyles = {
    none: '',
    simple: `border-[${design.border_width}px] border-solid`,
    ornate: `border-[${design.border_width}px] border-double`,
    double: `border-[${design.border_width}px] ring-4 ring-offset-4`
  };

  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'left': 'left-4 top-1/2 -translate-y-1/2',
    'right': 'right-4 top-1/2 -translate-y-1/2',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="certificate-designer">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Certificate Designer</h1>
          <p className="text-sm text-muted-foreground">Design and generate certificates for recipients</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" onClick={() => setShowBulkDialog(true)}>
            <Users className="w-4 h-4 mr-2" />
            Bulk Generate
          </Button>
          <Button onClick={handleSaveDesign} disabled={saving} style={{ backgroundColor: '#D4AF37' }}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Design
          </Button>
        </div>
      </div>

      <Alert className="border-blue-200 bg-blue-50">
        <Layout className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Image Size Guidelines:</strong><br />
          • Logo: 300x100 px (PNG transparent) | • Background: {dimensions.width * 10}x{dimensions.height * 10} px<br />
          • Seal: 200x200 px (PNG transparent) | • Signature: 200x80 px (PNG transparent)<br />
          • Recipient Photo: {design.recipient_photo_size}x{design.recipient_photo_size} px
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Design Settings */}
        <div className="space-y-6">
          <Tabs defaultValue="size" className="w-full">
            <TabsList className="grid w-full grid-cols-6 text-xs">
              <TabsTrigger value="size">Size</TabsTrigger>
              <TabsTrigger value="logos">Logos</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="photo">Photo</TabsTrigger>
              <TabsTrigger value="signatories">Signs</TabsTrigger>
              <TabsTrigger value="style">Style</TabsTrigger>
            </TabsList>

            {/* Size Tab */}
            <TabsContent value="size" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Template Style</CardTitle>
                  <CardDescription>Choose the overall look and feel of your certificate</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(templateStyles).map(([key, style]) => (
                      <div
                        key={key}
                        onClick={() => setDesign(prev => ({ ...prev, template_style: key }))}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-amber-400 ${
                          design.template_style === key ? 'border-amber-500 bg-amber-50' : 'border-gray-200'
                        }`}
                      >
                        <div className={`h-16 mb-3 rounded bg-gradient-to-br from-amber-50 to-white ${style.preview}`}></div>
                        <p className="font-semibold text-sm">{style.name}</p>
                        <p className="text-xs text-muted-foreground">{style.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Size & Orientation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Paper Size</Label>
                      <Select value={design.size} onValueChange={(v) => setDesign(prev => ({ ...prev, size: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="a4">A4 (297x210 mm)</SelectItem>
                          <SelectItem value="letter">Letter (279x216 mm)</SelectItem>
                          <SelectItem value="custom">Custom Size</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Orientation</Label>
                      <Select value={design.orientation} onValueChange={(v) => setDesign(prev => ({ ...prev, orientation: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="landscape">Landscape</SelectItem>
                          <SelectItem value="portrait">Portrait</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {design.size === 'custom' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Width (mm)</Label>
                        <Input
                          type="number"
                          value={design.custom_width}
                          onChange={(e) => setDesign(prev => ({ ...prev, custom_width: parseInt(e.target.value) || 297 }))}
                        />
                      </div>
                      <div>
                        <Label>Height (mm)</Label>
                        <Input
                          type="number"
                          value={design.custom_height}
                          onChange={(e) => setDesign(prev => ({ ...prev, custom_height: parseInt(e.target.value) || 210 }))}
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Background Image</Label>
                    <p className="text-xs text-muted-foreground mb-2">Size: {dimensions.width * 10}x{dimensions.height * 10} px</p>
                    <div className="flex gap-2">
                      <Input
                        value={design.background_image_url}
                        onChange={(e) => setDesign(prev => ({ ...prev, background_image_url: e.target.value }))}
                        placeholder="Paste URL or upload"
                        className="flex-1"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={el => fileInputRefs.current['background'] = el}
                        onChange={(e) => handleImageUpload('background', 'main', e.target.files?.[0])}
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRefs.current['background']?.click()}
                        disabled={uploading['background_main']}
                      >
                        {uploading['background_main'] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>Border Style</Label>
                    <Select value={design.border_style} onValueChange={(v) => setDesign(prev => ({ ...prev, border_style: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="simple">Simple</SelectItem>
                        <SelectItem value="ornate">Ornate (Double)</SelectItem>
                        <SelectItem value="double">Double with Ring</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Logos Tab */}
            <TabsContent value="logos" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>Logos</span>
                    <Button variant="outline" size="sm" onClick={addLogo}>
                      <Plus className="w-4 h-4 mr-1" /> Add Logo
                    </Button>
                  </CardTitle>
                  <CardDescription>Add multiple logos and position them</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {design.logos.map((logo, index) => (
                    <div key={logo.id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="font-semibold">Logo {index + 1}</Label>
                        {design.logos.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeLogo(logo.id)} className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Input
                          value={logo.url}
                          onChange={(e) => updateLogo(logo.id, 'url', e.target.value)}
                          placeholder="Paste URL or upload (300x100 px)"
                          className="flex-1"
                        />
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={el => fileInputRefs.current[`logo_${logo.id}`] = el}
                          onChange={(e) => handleImageUpload('logo', logo.id, e.target.files?.[0])}
                        />
                        <Button
                          variant="outline"
                          onClick={() => fileInputRefs.current[`logo_${logo.id}`]?.click()}
                          disabled={uploading[`logo_${logo.id}`]}
                        >
                          {uploading[`logo_${logo.id}`] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        </Button>
                      </div>

                      {logo.url && (
                        <img src={getImageUrl(logo.url)} alt="Logo" className="h-12 object-contain" />
                      )}
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Position</Label>
                          <Select value={logo.position} onValueChange={(v) => updateLogo(logo.id, 'position', v)}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="top-left">Top Left</SelectItem>
                              <SelectItem value="top-center">Top Center</SelectItem>
                              <SelectItem value="top-right">Top Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Size (px)</Label>
                          <Input
                            type="number"
                            value={logo.size}
                            onChange={(e) => updateLogo(logo.id, 'size', parseInt(e.target.value) || 80)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Certificate Content</CardTitle>
                  <CardDescription>Use {'{RECIPIENT_NAME}'} and {'{AWARD_CATEGORY}'} as placeholders</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Show Header (Org Name)</Label>
                    <Switch
                      checked={design.show_header}
                      onCheckedChange={(c) => setDesign(prev => ({ ...prev, show_header: c }))}
                    />
                  </div>
                  {design.show_header && (
                    <>
                      <div>
                        <Label>Organization Name</Label>
                        <Input
                          value={design.organization_name}
                          onChange={(e) => setDesign(prev => ({ ...prev, organization_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Chapter Name</Label>
                        <Input
                          value={design.chapter_name}
                          onChange={(e) => setDesign(prev => ({ ...prev, chapter_name: e.target.value }))}
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <Label>Certificate Title</Label>
                    <Input
                      value={design.certificate_title}
                      onChange={(e) => setDesign(prev => ({ ...prev, certificate_title: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Title Font Size (px)</Label>
                    <Input
                      type="number"
                      value={design.title_font_size}
                      onChange={(e) => setDesign(prev => ({ ...prev, title_font_size: parseInt(e.target.value) || 48 }))}
                    />
                  </div>
                  <div>
                    <Label>Introduction Text</Label>
                    <Input
                      value={design.intro_text}
                      onChange={(e) => setDesign(prev => ({ ...prev, intro_text: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Body Text (before award)</Label>
                    <Input
                      value={design.body_text}
                      onChange={(e) => setDesign(prev => ({ ...prev, body_text: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Conclusion Text</Label>
                    <Textarea
                      value={design.conclusion_text}
                      onChange={(e) => setDesign(prev => ({ ...prev, conclusion_text: e.target.value }))}
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Recipient Photo Tab */}
            <TabsContent value="photo" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recipient Photo</CardTitle>
                  <CardDescription>Include recipient's photo on the certificate</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Show Recipient Photo</Label>
                      <p className="text-xs text-muted-foreground">Display photo of the recipient</p>
                    </div>
                    <Switch
                      checked={design.show_recipient_photo}
                      onCheckedChange={(c) => setDesign(prev => ({ ...prev, show_recipient_photo: c }))}
                    />
                  </div>
                  
                  {design.show_recipient_photo && (
                    <>
                      <div>
                        <Label>Photo Size (px)</Label>
                        <Input
                          type="number"
                          value={design.recipient_photo_size}
                          onChange={(e) => setDesign(prev => ({ ...prev, recipient_photo_size: parseInt(e.target.value) || 100 }))}
                        />
                      </div>
                      <div>
                        <Label>Photo Position</Label>
                        <Select value={design.recipient_photo_position} onValueChange={(v) => setDesign(prev => ({ ...prev, recipient_photo_position: v }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top-center">Top Center</SelectItem>
                            <SelectItem value="left">Left Side</SelectItem>
                            <SelectItem value="right">Right Side</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Photo Shape</Label>
                        <Select value={design.recipient_photo_shape} onValueChange={(v) => setDesign(prev => ({ ...prev, recipient_photo_shape: v }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="circle">Circle</SelectItem>
                            <SelectItem value="square">Square</SelectItem>
                            <SelectItem value="rounded">Rounded</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Signatories Tab */}
            <TabsContent value="signatories" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>Signatories</span>
                    <Button variant="outline" size="sm" onClick={addSignatory}>
                      <Plus className="w-4 h-4 mr-1" /> Add Signatory
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Show Signature Lines</Label>
                    <Switch
                      checked={design.show_signature_lines}
                      onCheckedChange={(c) => setDesign(prev => ({ ...prev, show_signature_lines: c }))}
                    />
                  </div>

                  {design.signatories.map((sig, index) => (
                    <div key={sig.id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="font-semibold">Signatory {index + 1}</Label>
                        {design.signatories.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeSignatory(sig.id)} className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input
                            value={sig.name}
                            onChange={(e) => updateSignatory(sig.id, 'name', e.target.value)}
                            placeholder="Signatory Name"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Designation/Post</Label>
                          <Input
                            value={sig.designation}
                            onChange={(e) => updateSignatory(sig.id, 'designation', e.target.value)}
                            placeholder="President, Secretary, etc."
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs">Signature Image (200x80 px)</Label>
                        <div className="flex gap-2">
                          <Input
                            value={sig.signature_url}
                            onChange={(e) => updateSignatory(sig.id, 'signature_url', e.target.value)}
                            placeholder="Paste URL or upload"
                            className="flex-1"
                          />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={el => fileInputRefs.current[`sig_${sig.id}`] = el}
                            onChange={(e) => handleImageUpload('signature', sig.id, e.target.files?.[0])}
                          />
                          <Button
                            variant="outline"
                            onClick={() => fileInputRefs.current[`sig_${sig.id}`]?.click()}
                            disabled={uploading[`signature_${sig.id}`]}
                          >
                            {uploading[`signature_${sig.id}`] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          </Button>
                        </div>
                        {sig.signature_url && (
                          <img src={getImageUrl(sig.signature_url)} alt="Signature" className="h-10 mt-2 object-contain" />
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Seal/Stamp */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="font-semibold">Official Seal/Stamp</Label>
                      <Switch
                        checked={design.show_seal}
                        onCheckedChange={(c) => setDesign(prev => ({ ...prev, show_seal: c }))}
                      />
                    </div>
                    
                    {design.show_seal && (
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input
                            value={design.seal_image_url}
                            onChange={(e) => setDesign(prev => ({ ...prev, seal_image_url: e.target.value }))}
                            placeholder="Seal image URL (200x200 px)"
                            className="flex-1"
                          />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={el => fileInputRefs.current['seal'] = el}
                            onChange={(e) => handleImageUpload('seal', 'main', e.target.files?.[0])}
                          />
                          <Button
                            variant="outline"
                            onClick={() => fileInputRefs.current['seal']?.click()}
                            disabled={uploading['seal_main']}
                          >
                            {uploading['seal_main'] ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          </Button>
                        </div>
                        {!design.seal_image_url && (
                          <div>
                            <Label className="text-xs">Seal Text (if no image)</Label>
                            <Input
                              value={design.seal_text}
                              onChange={(e) => setDesign(prev => ({ ...prev, seal_text: e.target.value }))}
                            />
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Seal Size (px)</Label>
                            <Input
                              type="number"
                              value={design.seal_size}
                              onChange={(e) => setDesign(prev => ({ ...prev, seal_size: parseInt(e.target.value) || 80 }))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Position</Label>
                            <Select value={design.seal_position} onValueChange={(v) => setDesign(prev => ({ ...prev, seal_position: v }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="bottom-left">Bottom Left</SelectItem>
                                <SelectItem value="bottom-center">Bottom Center</SelectItem>
                                <SelectItem value="bottom-right">Bottom Right</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label>Show Date</Label>
                      <Switch
                        checked={design.show_date}
                        onCheckedChange={(c) => setDesign(prev => ({ ...prev, show_date: c }))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Style Tab */}
            <TabsContent value="style" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Colors & Style</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Background Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={design.background_color} onChange={(e) => setDesign(prev => ({ ...prev, background_color: e.target.value }))} className="w-12 h-10 p-1" />
                        <Input value={design.background_color} onChange={(e) => setDesign(prev => ({ ...prev, background_color: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <Label>Border Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={design.border_color} onChange={(e) => setDesign(prev => ({ ...prev, border_color: e.target.value }))} className="w-12 h-10 p-1" />
                        <Input value={design.border_color} onChange={(e) => setDesign(prev => ({ ...prev, border_color: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <Label>Title Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={design.title_color} onChange={(e) => setDesign(prev => ({ ...prev, title_color: e.target.value }))} className="w-12 h-10 p-1" />
                        <Input value={design.title_color} onChange={(e) => setDesign(prev => ({ ...prev, title_color: e.target.value }))} />
                      </div>
                    </div>
                    <div>
                      <Label>Accent Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={design.accent_color} onChange={(e) => setDesign(prev => ({ ...prev, accent_color: e.target.value }))} className="w-12 h-10 p-1" />
                        <Input value={design.accent_color} onChange={(e) => setDesign(prev => ({ ...prev, accent_color: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Live Preview */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Live Preview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="relative overflow-hidden mx-auto"
              style={{ 
                backgroundColor: design.background_color,
                borderColor: design.border_color,
                borderWidth: design.border_style !== 'none' ? design.border_width : 0,
                borderStyle: design.border_style === 'ornate' ? 'double' : 'solid',
                aspectRatio: aspectRatio,
                maxWidth: '100%',
                backgroundImage: design.background_image_url ? `url(${getImageUrl(design.background_image_url)})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 p-4 flex flex-col items-center justify-between text-center" style={{ fontSize: '0.5rem' }}>
                {/* Logos */}
                {design.logos.filter(l => l.url).map(logo => (
                  <div key={logo.id} className={`absolute ${positionClasses[logo.position]}`}>
                    <img src={getImageUrl(logo.url)} alt="Logo" style={{ height: logo.size / 4 }} className="object-contain" />
                  </div>
                ))}

                {/* Header */}
                {design.show_header && (
                  <div className="mt-8">
                    <p style={{ color: design.accent_color, fontSize: '0.6rem' }}>{design.organization_name}</p>
                    <p style={{ color: design.body_color, fontSize: '0.5rem' }}>{design.chapter_name}</p>
                  </div>
                )}

                {/* Recipient Photo */}
                {design.show_recipient_photo && (
                  <div 
                    className={`bg-gray-200 flex items-center justify-center ${design.recipient_photo_shape === 'circle' ? 'rounded-full' : design.recipient_photo_shape === 'rounded' ? 'rounded-lg' : ''}`}
                    style={{ 
                      width: design.recipient_photo_size / 4, 
                      height: design.recipient_photo_size / 4,
                      border: `2px solid ${design.accent_color}`
                    }}
                  >
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                )}

                {/* Body */}
                <div className="flex-1 flex flex-col items-center justify-center py-2 px-4">
                  <h1 style={{ color: design.title_color, fontSize: design.title_font_size / 6, fontWeight: 'bold' }}>
                    {design.certificate_title}
                  </h1>
                  <p style={{ color: design.body_color, marginTop: '0.25rem' }}>{design.intro_text}</p>
                  <p style={{ color: design.accent_color, fontWeight: 'bold', fontSize: '0.7rem', marginTop: '0.25rem' }}>John Doe</p>
                  <p style={{ color: design.body_color }}>{design.body_text}</p>
                  <p style={{ color: design.accent_color, fontWeight: 'bold', fontSize: '0.6rem' }}>Excellence Award</p>
                  <p style={{ color: design.body_color, maxWidth: '80%', fontSize: '0.4rem' }}>{design.conclusion_text}</p>
                </div>

                {/* Date */}
                {design.show_date && (
                  <p style={{ color: design.body_color, fontSize: '0.4rem' }}>
                    Date: {formatDate(new Date())}
                  </p>
                )}

                {/* Signatories */}
                {design.show_signature_lines && (
                  <div className="flex justify-around w-full px-4 mt-2">
                    {design.signatories.slice(0, 4).map(sig => (
                      <div key={sig.id} className="text-center">
                        {sig.signature_url && (
                          <img src={getImageUrl(sig.signature_url)} alt="Signature" className="h-4 mx-auto mb-1" />
                        )}
                        <div className="w-12 border-b mb-1" style={{ borderColor: design.body_color }}></div>
                        <p style={{ color: design.body_color, fontSize: '0.35rem', fontWeight: 'bold' }}>{sig.name || sig.designation}</p>
                        <p style={{ color: design.accent_color, fontSize: '0.3rem' }}>{sig.designation}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Seal */}
                {design.show_seal && (
                  <div className={`absolute ${positionClasses[design.seal_position]}`}>
                    {design.seal_image_url ? (
                      <img src={getImageUrl(design.seal_image_url)} alt="Seal" style={{ width: design.seal_size / 4, height: design.seal_size / 4 }} className="object-contain" />
                    ) : (
                      <div 
                        className="rounded-full border-2 flex items-center justify-center"
                        style={{ width: design.seal_size / 4, height: design.seal_size / 4, borderColor: design.accent_color }}
                      >
                        <span style={{ color: design.accent_color, fontSize: '0.25rem', fontWeight: 'bold' }}>{design.seal_text}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Generation Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Bulk Certificate Generation
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={bulkTab} onValueChange={setBulkTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="event">Event Participants</TabsTrigger>
              <TabsTrigger value="members">Club Members</TabsTrigger>
              <TabsTrigger value="excel">Upload Excel/CSV</TabsTrigger>
            </TabsList>

            {/* Event Participants Tab */}
            <TabsContent value="event" className="space-y-4">
              <div>
                <Label>Select Event</Label>
                <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.event_id} value={event.event_id}>
                        {event.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {nominees.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Select Recipients ({selectedNominees.length}/{nominees.length})</Label>
                    <Button variant="ghost" size="sm" onClick={handleSelectAllNominees}>
                      {selectedNominees.length === nominees.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nominees.map((nominee) => (
                          <TableRow key={nominee.submission_id} className="cursor-pointer" onClick={() => toggleNomineeSelection(nominee.submission_id)}>
                            <TableCell>
                              <Checkbox checked={selectedNominees.includes(nominee.submission_id)} onChange={() => {}} />
                            </TableCell>
                            <TableCell>{nominee.user_name}</TableCell>
                            <TableCell>{nominee.user_email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : selectedEvent ? (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertDescription className="text-amber-800">
                    No participants found for this event. Try selecting a different event.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="border-blue-200 bg-blue-50">
                  <AlertDescription className="text-blue-800">
                    Select an event to view its participants.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members" className="space-y-4">
              <Alert className="border-blue-200 bg-blue-50">
                <Users className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Generate certificates for club members. Great for membership certificates, appreciation awards, or recognition.
                </AlertDescription>
              </Alert>
              
              {members.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Select Members ({selectedMembers.length}/{members.length})</Label>
                    <Button variant="ghost" size="sm" onClick={handleSelectAllMembers}>
                      {selectedMembers.length === members.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Email</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {members.map((member) => (
                          <TableRow key={member.user_id} className="cursor-pointer" onClick={() => toggleMemberSelection(member.user_id)}>
                            <TableCell>
                              <Checkbox checked={selectedMembers.includes(member.user_id)} onChange={() => {}} />
                            </TableCell>
                            <TableCell>{member.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{member.role}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{member.email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <Alert className="border-amber-200 bg-amber-50">
                  <AlertDescription className="text-amber-800">
                    No members found. Add members from the Members section first.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>

            {/* Excel Tab */}
            <TabsContent value="excel" className="space-y-4">
              <Alert className="border-blue-200 bg-blue-50">
                <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <strong>CSV/Excel Format:</strong><br />
                  Required columns: <code>name</code>, <code>category</code><br />
                  Optional: <code>email</code>, <code>photo_url</code>
                </AlertDescription>
              </Alert>
              
              <div>
                <Label>Upload CSV/Excel File</Label>
                <div className="flex gap-2 mt-2">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    ref={excelInputRef}
                    onChange={(e) => handleExcelUpload(e.target.files?.[0])}
                  />
                  <Button variant="outline" onClick={() => excelInputRef.current?.click()} className="w-full">
                    <Upload className="w-4 h-4 mr-2" />
                    Select File
                  </Button>
                </div>
              </div>

              {excelData.length > 0 && (
                <div>
                  <Label>Loaded Records ({excelData.length})</Label>
                  <div className="border rounded-lg max-h-48 overflow-y-auto mt-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Category</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {excelData.slice(0, 10).map((row, i) => (
                          <TableRow key={i}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.category}</TableCell>
                          </TableRow>
                        ))}
                        {excelData.length > 10 && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-muted-foreground">
                              +{excelData.length - 10} more records
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
            <Button 
              variant="outline"
              onClick={handleDownloadAllCertificates}
              disabled={generating || (bulkTab === 'event' && selectedNominees.length === 0) || (bulkTab === 'members' && selectedMembers.length === 0) || (bulkTab === 'excel' && excelData.length === 0)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download All as ZIP
            </Button>
            <Button 
              onClick={handleBulkGenerate}
              disabled={generating || (bulkTab === 'event' && selectedNominees.length === 0) || (bulkTab === 'members' && selectedMembers.length === 0) || (bulkTab === 'excel' && excelData.length === 0)}
              style={{ backgroundColor: '#D4AF37' }}
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Generate & Email ({bulkTab === 'event' ? selectedNominees.length : bulkTab === 'members' ? selectedMembers.length : excelData.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Certificate Full Preview</span>
              <Button variant="outline" onClick={handleDownloadSinglePDF}>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div 
            className="relative overflow-hidden mx-auto border shadow-lg"
            style={{ 
              backgroundColor: design.background_color,
              borderColor: design.border_color,
              borderWidth: design.border_style !== 'none' ? design.border_width : 0,
              borderStyle: design.border_style === 'ornate' ? 'double' : 'solid',
              width: '100%',
              aspectRatio: aspectRatio,
              backgroundImage: design.background_image_url ? `url(${getImageUrl(design.background_image_url)})` : 'none',
              backgroundSize: 'cover'
            }}
          >
            <div className="absolute inset-0 p-8 flex flex-col items-center justify-between text-center">
              {/* Logos */}
              {design.logos.filter(l => l.url).map(logo => (
                <div key={logo.id} className={`absolute ${positionClasses[logo.position]}`} style={{ top: '1rem' }}>
                  <img src={getImageUrl(logo.url)} alt="Logo" style={{ height: logo.size }} className="object-contain" />
                </div>
              ))}

              {/* Header */}
              {design.show_header && (
                <div className="mt-16">
                  <p className="text-lg font-semibold" style={{ color: design.accent_color }}>{design.organization_name}</p>
                  <p style={{ color: design.body_color }}>{design.chapter_name}</p>
                </div>
              )}

              {/* Recipient Photo */}
              {design.show_recipient_photo && (
                <div 
                  className={`bg-gray-200 flex items-center justify-center ${design.recipient_photo_shape === 'circle' ? 'rounded-full' : design.recipient_photo_shape === 'rounded' ? 'rounded-lg' : ''}`}
                  style={{ 
                    width: design.recipient_photo_size, 
                    height: design.recipient_photo_size,
                    border: `4px solid ${design.accent_color}`
                  }}
                >
                  <User className="w-12 h-12 text-gray-400" />
                </div>
              )}

              {/* Body */}
              <div className="flex-1 flex flex-col items-center justify-center py-4">
                <h1 className="font-bold" style={{ color: design.title_color, fontSize: design.title_font_size }}>
                  {design.certificate_title}
                </h1>
                <p className="text-lg mt-4" style={{ color: design.body_color }}>{design.intro_text}</p>
                <p className="text-3xl font-bold mt-2" style={{ color: design.accent_color }}>Sample Recipient Name</p>
                <p className="text-lg mt-2" style={{ color: design.body_color }}>{design.body_text}</p>
                <p className="text-xl font-semibold mt-1" style={{ color: design.accent_color }}>Excellence in Social Service Award</p>
                <p className="text-base mt-4 max-w-lg" style={{ color: design.body_color }}>{design.conclusion_text}</p>
              </div>

              {/* Date */}
              {design.show_date && (
                <p className="text-base mb-4" style={{ color: design.body_color }}>
                  Date: {formatDate(new Date())}
                </p>
              )}

              {/* Signatories */}
              {design.show_signature_lines && (
                <div className="flex justify-around w-full px-16 mb-8">
                  {design.signatories.map(sig => (
                    <div key={sig.id} className="text-center">
                      {sig.signature_url && (
                        <img src={getImageUrl(sig.signature_url)} alt="Signature" className="h-12 mx-auto mb-2" />
                      )}
                      <div className="w-32 border-b-2 mb-2" style={{ borderColor: design.body_color }}></div>
                      <p className="font-semibold" style={{ color: design.body_color }}>{sig.name || sig.designation}</p>
                      <p className="text-sm" style={{ color: design.accent_color }}>{sig.designation}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Seal */}
              {design.show_seal && (
                <div className={`absolute ${positionClasses[design.seal_position]}`}>
                  {design.seal_image_url ? (
                    <img src={getImageUrl(design.seal_image_url)} alt="Seal" style={{ width: design.seal_size, height: design.seal_size }} className="object-contain" />
                  ) : (
                    <div 
                      className="rounded-full border-4 flex items-center justify-center"
                      style={{ width: design.seal_size, height: design.seal_size, borderColor: design.accent_color }}
                    >
                      <span className="font-bold" style={{ color: design.accent_color, fontSize: design.seal_size / 6 }}>{design.seal_text}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificateDesigner;
